import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate } from 'react-router';

import type { UserLanguage } from '~/api/user/types';

import { useLogin, useLogout } from '~/api/auth';
import { userQueryOptions } from '~/api/user/useUser';
import { env } from '~/constants/env';
import { useCraftLocalStorage } from '~/hooks/useCraftLocalStorage';
import { CommandMenuItem } from '~/providers/CommandMenuProvider/CommandMenuItem';
import { routes } from '~/providers/RouterProvider/router.routes';

/** The accounts `DevSeeder` creates in the backend. */
const devAccounts = [
  'full',
  'onboarding',
  'invitor-discount',
  'invitee-discount',
  'vat-exempt-above-limit',
  'copyright-above-limit',
  'peppol-unregistered',
  'peppol-self-billing-unsupported',
  'early-adopter',
];

export const RootCommandMenuItems = () => {
  const loginMutation = useLogin();
  const logoutMutation = useLogout();

  const { i18n } = useTranslation();

  const [, setLanguage] = useCraftLocalStorage('language');
  const isCiMode = i18n.language === 'cimode';

  const navigate = useNavigate();
  const isDesignSystemRoute = !!useMatch(routes.designSystem);
  const isLoginRoute = !!useMatch(routes.login);

  const queryClient = useQueryClient();
  // A non-subscribing read on purpose: subscribing here would put an observer on the user query
  // outside the protected tree, which would refetch against a dead session after a logout.
  const isAuthenticated = queryClient.getQueryData(userQueryOptions.queryKey) !== undefined;

  return (
    <>
      {isLoginRoute && env.IS_LOCAL_DEV_ENV && (
        <CommandMenuItem
          group="General"
          items={devAccounts.map((slug) => ({
            label: slug.charAt(0).toUpperCase() + slug.slice(1).replaceAll('-', ' '),
            onClick: () =>
              loginMutation.mutate(
                { emailAddress: `developer${slug === 'full' ? '' : `+${slug}`}@creativeshelter.be`, password: 'password' },
                { onSuccess: () => navigate(routes.dashboard, { replace: true }) },
              ),
          }))}
          label="Log in as"
          shortcut="X"
        />
      )}

      {isAuthenticated && !isLoginRoute && env.IS_LOCAL_DEV_ENV && (
        <CommandMenuItem group="General" label="Log out" onClick={() => logoutMutation.mutate()} shortcut="X" />
      )}

      <CommandMenuItem
        group="General"
        items={(['nl', 'fr', 'en', 'cimode'] as UserLanguage[]).map((language) => ({
          label: language,
          onClick: () => setLanguage(language),
        }))}
        label="Switch language"
        shortcut="L"
      />

      <CommandMenuItem
        group="General"
        label={isDesignSystemRoute ? 'Go back to Craft' : 'Go to design system'}
        onClick={() => navigate(isDesignSystemRoute ? routes.dashboard : routes.designSystem)}
        shortcut="D"
      />

      <CommandMenuItem
        group="General"
        label="Refetch active queries"
        onClick={() => queryClient.refetchQueries({ type: 'active' })}
        shortcut="Q"
      />

      {isCiMode && (
        <style>
          {`
            * {
              word-break: break-all !important;
              text-transform: none !important;
            }
          `}
        </style>
      )}
    </>
  );
};
